/**
 * Full-screen page-transition overlay with a "curtain + logo draw" animation
 * and an integrated numbered loader.
 *
 * Nothing here animates from main-thread JS frames: the curtains and fades
 * are WAAPI transform/opacity animations (compositor thread), the logo
 * draws on an OffscreenCanvas in a worker (see Logo.tsx), and the counter's
 * wheels play a trajectory baked ahead of time as WAAPI keyframes (see
 * NumberedLoader.tsx). The new page mounts while the logo draws, and its
 * long tasks used to freeze them.
 *
 * **Animation sequence (SPA navigation):**
 *  1. `coverPage()` — blocks scale left-to-right to cover the page.
 *  2. **Immediately** after blocks close: `navigate(url)` + loader starts counting.
 *  3. Logo draws stroke-by-stroke (off the main thread, while the page loads).
 *  4. New page loads behind curtain and calls `signalPageReady()`.
 *  5. **Dual gate:** if the page is still loading once the logo is drawn,
 *     it spins until the page is ready, then settles on the fully formed
 *     logo. The logo says when it will rest, and the loader counter
 *     paces its run to 100 to land at that moment; both must be done.
 *  6. Logo overlay fades out + loader fades out simultaneously.
 *  7. `revealPage()` — blocks scale right-to-left to uncover the new page.
 *  8. Page entrance animations fire (after `isTransitioning` becomes false).
 *
 * **Initial page load:**
 *  Curtains start already closed → logo animates → loader counts to 100 →
 *  reveal page → entrance animations fire.
 *
 * **First load of / (the carousel)** is the exception: no curtain and no
 *  logo. The carousel's own entrance is the loader (state/entrance.ts):
 *  this component only paints the off-white backdrop from the first
 *  frame, which the carousel crossfades away as it unrolls. With reduced
 *  motion the carousel is simply there.
 *
 * **Arriving at / from outside the carousel** (the book, the 404) plays
 *  that entrance too. By link: the curtain closes in the loader's
 *  off-white, with no logo and no counter of its own (the entrance brings
 *  the counter), the entrance is put in "boot" and the carousel mounts
 *  in entrance mode under the curtain, and the curtain is dropped once
 *  the backdrop has painted: the same colour, so nothing shows. By
 *  browser back: no curtain, the entrance is put in "boot" before the
 *  router renders, as on a cold load. Hops within the carousel page (a
 *  panel closing) have neither curtain nor entrance.
 *
 * **Link interception:** on mount, all internal `<a href="/">` links get a click
 * listener that prevents default navigation and calls `coverPage()` instead.
 * Listeners are re-attached on every pathname change to catch dynamically
 * rendered links.
 */
import Logo, { type LogoHandle } from "./Logo";
import { LOGO_TOTAL_MS } from "./logoFrame";
import NumberedLoader from "./NumberedLoader.tsx";
import EntranceLoader from "./EntranceLoader";
import {
    ReactNode,
    useCallback,
    useEffect,
    useRef, useState,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useViewportBreakpoint } from "../../hooks/useViewportBreakpoint";
import { PageTransitionContext } from "./PageTransitionContext";
import { getPanelSlugFromPath, isCarouselPath } from "../Portfolio/CaseStudy/registry";
import { getThemeForPath, NOT_FOUND, resolveRouteKey, ROUTE_THEMES, type RouteTheme } from "./routeThemes";
import { ROUTES } from "../../routes";
import { animationConfig } from "../../config/animation";
import { markAppPainted, setRootBackground, tracksScreenEdges } from "../../utils/edgeToEdge";
import { useAtomValue } from "jotai";
import { ENTRANCE_BACKDROP_VAR, entrancePhaseAtom, entranceStore, isLoaderPhase, setEntrancePhase } from "../../state/entrance";

const PT = animationConfig.pageTransition;
const ENTRANCE = animationConfig.portfolio.entrance;

type SweepTiming = { durationS: number; staggerS: number; easing: string };

/**
 * Staggered scaleX sweep of the curtain blocks via the Web Animations API.
 * WAAPI transform animations run on the compositor thread, so the sweep
 * stays smooth while the main thread is busy (the outgoing page's R3F
 * loop during the cover, the incoming scene's first renders during the
 * reveal). GSAP writes inline styles from JS every frame and can't.
 * Resolves once every block has landed, with the end state committed
 * to inline styles so no fill-forwards animations pile up.
 */
function sweepBlocks(
    blocks: HTMLDivElement[],
    from: number,
    to: number,
    origin: "left" | "right",
    { durationS, staggerS, easing }: SweepTiming,
): Promise<void> {
    const anims = blocks.map((block, i) => {
        block.style.transformOrigin = origin;
        return block.animate(
            [
                { transform: `translateZ(0) scaleX(${from})` },
                { transform: `translateZ(0) scaleX(${to})` },
            ],
            // "both": blocks waiting on their stagger delay hold `from`.
            { duration: durationS * 1000, delay: i * staggerS * 1000, easing, fill: "both" },
        );
    });
    return Promise.all(anims.map((a) => a.finished))
        .catch(() => {}) // a block removed mid-sweep (resize rebuild) cancels its animation
        .then(() => {
            blocks.forEach((block, i) => {
                block.style.transform = `translateZ(0) scaleX(${to})`;
                anims[i].cancel();
            });
        });
}

/** Opacity fade on the compositor (see `sweepBlocks`), committed on finish. */
function fadeTo(el: HTMLElement, to: number, { durationS, easing }: { durationS: number; easing: string }) {
    const from = getComputedStyle(el).opacity;
    const anim = el.animate([{ opacity: from }, { opacity: String(to) }], {
        duration: durationS * 1000,
        easing,
        fill: "forwards",
    });
    return anim.finished.catch(() => {}).then(() => {
        el.style.opacity = String(to);
        anim.cancel();
    });
}

/** Actual page background color per route: the root background (html bg,
 *  desktop Safari's toolbar colour, and what iOS Safari blends its bars
 *  into, see utils/edgeToEdge). Panel paths (/about, /<project>) aren't
 *  listed: on iOS they use their curtain `bg`, which is their panel
 *  colour; elsewhere they fall through to the rgb(28,28,28) default, the
 *  carousel behind. */
const PAGE_BG: Record<string, string> = {
    [ROUTES.index]: "rgb(28,28,28)",
    [NOT_FOUND]: "rgb(28,28,28)",
    [ROUTES.book]: "#f5f5f0",
};

function setPageBackground(path: string) {
    const key = resolveRouteKey(path);
    const bg = PAGE_BG[key]
        ?? (tracksScreenEdges && getPanelSlugFromPath(key) !== null ? ROUTE_THEMES[key]?.bg : undefined)
        ?? "rgb(28,28,28)";
    setRootBackground(bg);
}

/** The carousel and its panels are one page (no curtain between them;
 *  the panel manages the root colour), so they count as the same route. */
const routeGroup = (path: string) => (isCarouselPath(path) ? ROUTES.index : path);

const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Arriving at the carousel from outside it (see the file comment):
 *  the carousel's entrance plays. */
const arrivesAtCarousel = (from: string, to: string) =>
    to === ROUTES.index && !isCarouselPath(from) && !reducedMotion();

/** Maximum time (ms) to wait for a page to signal ready before forcing reveal. */
const READY_TIMEOUT_MS = PT.readyTimeoutMs;

/** Extra wait, past the longest the logo can take to reach fully formed
 *  once asked (one full draw), before the reveal gate stops waiting on it,
 *  so a logo that never reports back (worker crash) can't keep the curtain
 *  shut. */
const LOGO_SAFETY_MS = 1500;

type PageTransitionProps = {
    children: ReactNode;
};

export default function PageTransition({ children }: PageTransitionProps) {
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const overlayRef = useRef<HTMLDivElement | null>(null);
    const logoOverlayRef = useRef<HTMLDivElement | null>(null);
    const logoRef = useRef<LogoHandle | null>(null);
    const blocksRef = useRef<HTMLDivElement[]>([]);
    /** Route the idle branch last matched the root colour to. */
    const lastIdlePathRef = useRef(window.location.pathname);

    // Start as transitioning so initial load shows curtains closed
    const [isTransitioningState, setIsTransitioningState] = useState(true);
    const isTransitioning = useRef(true);
    const isInitialLoad = useRef(true);

    // ── Loader state ─────────────────────────────────────────────────
    const [showLoader, setShowLoader] = useState(false);
    const [pageReady, setPageReady] = useState(false);
    const [loaderColor, setLoaderColor] = useState(
        () => getThemeForPath(window.location.pathname).stroke,
    );
    const [loaderFading, setLoaderFading] = useState(false);
    /** When the logo will rest fully formed (performance.now() ms): the
     *  counter lands on 100 then. Null until the logo has been told to
     *  finish and has answered. */
    const [logoRestsAtMs, setLogoRestsAtMs] = useState<number | null>(null);
    const waitingForReady = useRef(false);
    const readyTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    // ── Dual-gate refs ───────────────────────────────────────────────
    const logoAnimDone = useRef(false);
    const loaderCountDone = useRef(false);
    /** Bumped per transition so a stale finish or safety timer can't open
     *  the gate of a later one. */
    const logoRunRef = useRef(0);
    /** The run whose logo has been told to finish, so it's told once. */
    const logoFinishRunRef = useRef(-1);

    // Overlay background: covers content on the very first frame
    // (before useEffect creates blocks). Cleared after block setup. Not
    // on the carousel: it loads behind its entrance backdrop, and with
    // reduced motion is simply there on the dark page, where one frame
    // of the curtain's off-white would be a flash of its own.
    const [overlayBg, setOverlayBg] = useState(() =>
        window.location.pathname === ROUTES.index
            ? "transparent"
            : getThemeForPath(window.location.pathname).bg,
    );

    const isSmallScreen = useViewportBreakpoint(768);
    const numberOfBlocks = isSmallScreen ? 6 : 10;
    const logoScale = isSmallScreen ? 1 : 1.3;

    // ── signalPageReady — pages call this when content is loaded ─────
    const signalPageReady = useCallback(() => {
        if (!waitingForReady.current) return;
        setPageReady(true);
    }, []);

    // ── helpers ──────────────────────────────────────────────────────

    const applyOverlayTheme = (theme: RouteTheme) => {
        blocksRef.current.forEach((block) => {
            block.style.backgroundColor = theme.bg;
        });

        if (logoOverlayRef.current) {
            logoOverlayRef.current.style.backgroundColor = theme.bg;
        }
    };

    const revealPage = useCallback(() => {
        // iOS: the new page shows as soon as the blocks start opening.
        if (tracksScreenEdges) setPageBackground(window.location.pathname);

        sweepBlocks(blocksRef.current, 1, 0, "right", PT.curtainReveal).then(() => {
            blocksRef.current.forEach((b) => { b.style.backgroundColor = "transparent"; });
            if (logoOverlayRef.current) logoOverlayRef.current.style.backgroundColor = "transparent";

            if (!tracksScreenEdges) setPageBackground(window.location.pathname);

            isTransitioning.current = false;
            setIsTransitioningState(false);
        });
    }, []);

    const revealPageRef = useRef(revealPage);
    revealPageRef.current = revealPage;

    // ── checkReveal — dual gate: both logo done AND loader at 100 ────
    const checkReveal = useCallback(() => {
        if (!logoAnimDone.current || !loaderCountDone.current) return;

        // Both conditions met — fade logo + loader, then open curtains
        setLoaderFading(true);

        const logoOverlayEl = logoOverlayRef.current;
        const faded = logoOverlayEl ? fadeTo(logoOverlayEl, 0, PT.loaderFadeOut) : Promise.resolve();
        faded.then(() => {
            logoRef.current?.reset();
            setShowLoader(false);
            setLoaderFading(false);
            setPageReady(false);
            setLogoRestsAtMs(null);
            waitingForReady.current = false;
            if (readyTimeoutRef.current) {
                clearTimeout(readyTimeoutRef.current);
                readyTimeoutRef.current = undefined;
            }
            revealPageRef.current();
        });
    }, []);

    const checkRevealRef = useRef(checkReveal);
    checkRevealRef.current = checkReveal;

    // ── startLogo — overlay on, logo draws (then spins) until the page is ready
    const startLogo = useCallback((theme: RouteTheme) => {
        if (logoOverlayRef.current) logoOverlayRef.current.style.opacity = "1";
        logoRef.current?.start(theme);
    }, []);

    // ── finishLogo — the page is ready ───────────────────────────────
    // The logo settles on fully formed from wherever it is (drawing or
    // spinning) and reports when it will rest; the counter's run to 100
    // is paced to land at that moment (NumberedLoader's `finishAtMs`), so
    // the two finish together. The gate still waits for both, so the
    // curtains open on the full logo.
    const finishLogo = useCallback(() => {
        const run = logoRunRef.current;
        if (logoFinishRunRef.current === run) return;
        logoFinishRunRef.current = run;

        const markLogoDone = () => {
            if (run !== logoRunRef.current || logoAnimDone.current) return;
            logoAnimDone.current = true;
            checkRevealRef.current();
        };
        const logo = logoRef.current;
        if (!logo) return markLogoDone();
        logo.finish({
            restsAt: (atMs) => {
                if (run === logoRunRef.current) setLogoRestsAtMs(atMs);
            },
            rested: markLogoDone,
        });
        setTimeout(markLogoDone, LOGO_TOTAL_MS + LOGO_SAFETY_MS);
    }, []);

    useEffect(() => {
        if (pageReady) finishLogo();
    }, [pageReady, finishLogo]);

    // ── handleCountComplete — loader counter reached 100 ─────────────
    const handleCountComplete = useCallback(() => {
        if (loaderCountDone.current) return;
        loaderCountDone.current = true;
        finishLogo(); // already under way unless the ready signal was missed
        checkRevealRef.current();
    }, [finishLogo]);

    // ── createBlocks — build curtain block elements ──────────────────
    const createBlocks = useCallback(
        (container: HTMLDivElement, initialScaleX: number) => {
            container.innerHTML = "";
            blocksRef.current = [];

            for (let i = 0; i < numberOfBlocks; i++) {
                const block = document.createElement("div");
                block.className = "flex-1 h-full origin-left scale-x-0";

                // Tiny overlap to kill hairlines
                if (i !== 0) block.style.marginLeft = "-5px";

                // Helps Safari keep transforms stable
                block.style.willChange = "transform";
                block.style.transformOrigin = "left";
                block.style.transform = `translateZ(0) scaleX(${initialScaleX})`;

                container.appendChild(block);
                blocksRef.current.push(block);
            }
        },
        [numberOfBlocks],
    );

    /** Takes the curtain away at once, no sweep: for the hand-over to the
     *  carousel's entrance, whose backdrop is the curtain's colour. */
    const dropCurtain = useCallback(() => {
        if (overlayRef.current) createBlocks(overlayRef.current, 0);
        blocksRef.current.forEach((b) => { b.style.backgroundColor = "transparent"; });
        if (logoOverlayRef.current) logoOverlayRef.current.style.backgroundColor = "transparent";
        setOverlayBg("transparent");
        logoRef.current?.reset();
        isTransitioning.current = false;
        setIsTransitioningState(false);
    }, [createBlocks]);

    // ── coverPage ────────────────────────────────────────────────────
    const coverPage = useCallback(
        (url: string) => {
            const theme = getThemeForPath(url);
            applyOverlayTheme(theme);
            setLoaderColor(theme.stroke);
            // Desktop: one toolbar colour change per navigation, up front.
            if (!tracksScreenEdges) setPageBackground(url);

            // Reset dual gate
            logoAnimDone.current = false;
            logoRunRef.current++;
            loaderCountDone.current = false;
            setLogoRestsAtMs(null);

            // Arriving at the carousel from outside it: the curtain hands
            // over to the carousel's entrance (see the file comment).
            const entranceHop = arrivesAtCarousel(window.location.pathname, url);

            // Phase 1: curtain blocks close
            sweepBlocks(blocksRef.current, 0, 1, "left", PT.curtainCover).then(() => {
                // iOS: curtain fully closed, the bars take its colour.
                if (tracksScreenEdges) setRootBackground(theme.bg);

                if (entranceHop) {
                    // The loader's backdrop goes under the curtain (the
                    // entrance's phase mounts it), the carousel mounts in
                    // entrance mode, and the curtain is dropped once the
                    // backdrop has painted. The root colour is what iOS
                    // Safari blends its bars into: the backdrop's, until
                    // the carousel crossfades it.
                    if (tracksScreenEdges) setRootBackground(ENTRANCE.backdrop.color);
                    setEntrancePhase("boot");
                    navigate(url);
                    requestAnimationFrame(() => requestAnimationFrame(dropCurtain));
                    return;
                }

                // Navigate + start loader IMMEDIATELY after blocks close
                waitingForReady.current = true;
                setShowLoader(true);
                setPageReady(false);
                navigate(url);

                // Safety timeout
                readyTimeoutRef.current = setTimeout(() => {
                    if (waitingForReady.current) {
                        setPageReady(true);
                    }
                }, READY_TIMEOUT_MS);

                // Phase 2: logo draws while the new page mounts
                startLogo(theme);
            });
        },
        [navigate, startLogo, dropCurtain],
    );

    // Browser back or forward into the carousel from outside it: the
    // entrance plays, as on a cold load. Decided here, in the capture
    // phase, so it is set before the router renders the carousel, which
    // reads it once at mount.
    useEffect(() => {
        const onPop = () => {
            if (isTransitioning.current) return;
            if (!arrivesAtCarousel(lastIdlePathRef.current, window.location.pathname)) return;
            setEntrancePhase("boot");
            if (tracksScreenEdges) setRootBackground(ENTRANCE.backdrop.color);
        };
        window.addEventListener("popstate", onPop, true);
        return () => window.removeEventListener("popstate", onPop, true);
    }, []);

    // ── effect ──────────────────────────────────────────────────────
    // Runs on pathname change and when transition state or layout (e.g. numberOfBlocks from resize) changes.
    // Cleanup always runs before the next run, so link listeners are removed before we re-query and re-attach —
    // this avoids duplicate listeners when pathname or other deps update.
    useEffect(() => {
        if (!overlayRef.current) return;

        // Leaving the carousel while its first-load entrance is still
        // playing (browser back, say): end the entrance, or its backdrop
        // and loader would stay mounted over the next page. Nothing else
        // sees that route change.
        if (!isInitialLoad.current && !isCarouselPath(pathname)) {
            const phase = entranceStore.get(entrancePhaseAtom);
            if (phase !== "none" && phase !== "done") setEntrancePhase("done");
        }

        // The app's first frame is committed, and it covers the page (the
        // entrance backdrop, the closed curtain): the body may take its
        // colour now. Until here the root alone showed, in the colour
        // index.html chose for the address (routeThemes.ts).
        if (isInitialLoad.current) markAppPainted();

        if (isInitialLoad.current && pathname === ROUTES.index) {
            // ─── First load of the carousel: no curtain ───────────────
            // The carousel plays its own entrance (see the file comment);
            // with reduced motion it is simply there. Blocks are built
            // open so a later route change can close them as usual.
            isInitialLoad.current = false;
            createBlocks(overlayRef.current, 0);
            setOverlayBg("transparent");
            const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
            if (reduced) {
                setPageBackground(pathname);
            } else {
                setEntrancePhase("boot");
                // The root colour is what iOS Safari blends its bars into:
                // the loader's off-white, until the carousel crossfades it.
                if (tracksScreenEdges) setRootBackground(ENTRANCE.backdrop.color);
                else setPageBackground(pathname);
            }
            isTransitioning.current = false;
            setIsTransitioningState(false);
        } else if (isInitialLoad.current) {
            // ─── Initial page load: curtains start already closed ──────
            isInitialLoad.current = false;

            // Create blocks covering the page (scaleX: 1)
            createBlocks(overlayRef.current, 1);

            const theme = getThemeForPath(pathname);
            applyOverlayTheme(theme);
            setLoaderColor(theme.stroke);
            if (tracksScreenEdges) setRootBackground(theme.bg);

            // Clear overlay bg now that blocks provide the background
            setOverlayBg("transparent");

            // Start loader + waiting for page to signal ready
            waitingForReady.current = true;
            setShowLoader(true);
            setPageReady(false);
            logoAnimDone.current = false;
            logoRunRef.current++;
            loaderCountDone.current = false;
            setLogoRestsAtMs(null);

            // Start the logo now, as on a navigation. Its worker is inlined
            // and draws off the main thread, so the page's boot work can't
            // delay or stall it.
            startLogo(theme);

            // Safety timeout
            readyTimeoutRef.current = setTimeout(() => {
                if (waitingForReady.current) {
                    setPageReady(true);
                }
            }, READY_TIMEOUT_MS);

        } else if (!isTransitioning.current) {
            // ─── Not transitioning (safety fallback): create hidden blocks
            createBlocks(overlayRef.current, 0);

            // blank logo for the next transition
            logoRef.current?.reset();
        }
        // When idle: backgrounds cleared, and the root colour matched to a
        // route reached without the curtain (browser back/forward). Only on
        // an actual route change, so a resize re-run can't undo a colour
        // Index or a case study set.
        if (!isTransitioning.current) {
            blocksRef.current.forEach((b) => { b.style.backgroundColor = "transparent"; });
            if (logoOverlayRef.current) logoOverlayRef.current.style.backgroundColor = "transparent";
            if (routeGroup(pathname) !== routeGroup(lastIdlePathRef.current)) {
                // Under the entrance the root colour is the loader's
                // backdrop (the carousel crossfades it), not the page's.
                if (isLoaderPhase(entranceStore.get(entrancePhaseAtom))) {
                    if (tracksScreenEdges) setRootBackground(ENTRANCE.backdrop.color);
                } else {
                    setPageBackground(pathname);
                }
            }
            lastIdlePathRef.current = pathname;
        }

        // ─── Link interception ────────────────────────────────────────

        const handleRouteChange = (url: string) => {
            if (isTransitioning.current) return;
            isTransitioning.current = true;
            setIsTransitioningState(true);
            coverPage(url);
        };

        // One listener on the document, in the capture phase, rather than
        // one per link found when this effect runs: panels and pages mount
        // lazily, so links such as the About panel's door to the book, or
        // the 404's way back, appear after any snapshot and would navigate
        // without the curtain. Capture runs before React's own delegated
        // handlers, so preventDefault() here makes react-router's <Link>
        // stand down. Modified clicks (new tab, etc.) are left to the
        // browser.
        const handleLinkClick = (e: MouseEvent) => {
            if (e.defaultPrevented || e.button !== 0) return;
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            const target = (e.target as Element | null)?.closest?.('a[href^="/"]') as HTMLAnchorElement | null;
            if (!target || (target.target && target.target !== "_self")) return;

            e.preventDefault();

            const href = target.href;
            const url = new URL(href).pathname;

            if (url === pathname) return;

            // Hops within the carousel page (opening the About panel from
            // the carousel, leaving a case study or About for the
            // carousel): skip the GSAP curtain and just navigate. Portfolio
            // stays mounted, so its panel slides itself in / out — matching
            // arrow / Esc / browser-back behavior.
            if (routeGroup(url) === ROUTES.index && routeGroup(pathname) === ROUTES.index) {
                navigate(url);
                return;
            }

            handleRouteChange(url);
        };

        document.addEventListener("click", handleLinkClick, true);
        return () => {
            document.removeEventListener("click", handleLinkClick, true);
        };
    }, [pathname, coverPage, revealPage, createBlocks, startLogo, isTransitioningState, navigate]);

    // Clean up safety timeout on unmount so we never fire setPageReady after unmount.
    useEffect(() => {
        return () => {
            if (readyTimeoutRef.current) {
                clearTimeout(readyTimeoutRef.current);
            }
        };
    }, []);

    // ── render ───────────────────────────────────────────────────────

    // The portfolio entrance's backdrop: painted here, not by the page, so
    // it is there before the page chunk arrives. Its opacity is a CSS
    // variable the carousel's render loop writes while it unrolls.
    const entrancePhase = useAtomValue(entrancePhaseAtom);
    const showEntranceBackdrop = entrancePhase !== "none" && entrancePhase !== "done";

    return (
        <PageTransitionContext.Provider value={{ isTransitioning: isTransitioningState, signalPageReady }}>
            {showEntranceBackdrop && (
                <div
                    className="stage-bleed pointer-events-none"
                    style={{
                        backgroundColor: ENTRANCE.backdrop.color,
                        opacity: `var(${ENTRANCE_BACKDROP_VAR}, 1)`,
                    }}
                    aria-hidden="true"
                />
            )}
            {showEntranceBackdrop && <EntranceLoader />}
            {/* transition overlay. stage-bleed, not fixed: it covers the
                screen under iOS Safari's bars too (utils/edgeToEdge). */}
            <div
                ref={overlayRef}
                className={`stage-bleed flex z-[20] ${
                    isTransitioningState ? "pointer-events-auto" : "pointer-events-none"
                }`}
                style={{ backgroundColor: overlayBg }}
                aria-hidden="true"
            />

            {/* logo overlay */}
            <div
                ref={logoOverlayRef}
                className={`stage-bleed flex items-center justify-center py-[var(--bleed)] z-[30] opacity-0 ${
                    isTransitioningState ? "pointer-events-auto" : "pointer-events-none"
                }`}
                aria-hidden="true"
            >
                <Logo ref={logoRef} scale={logoScale} />
            </div>

            {/* Numbered loader — bottom-right, above logo overlay */}
            {showLoader && (
                <NumberedLoader
                    complete={pageReady}
                    finishAtMs={logoRestsAtMs ?? undefined}
                    color={loaderColor}
                    fading={loaderFading}
                    onCountComplete={handleCountComplete}
                />
            )}

            {children}
        </PageTransitionContext.Provider>
    );
}
